import { Node, mergeAttributes, type Command } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React from 'react';

// Tiptap HTML Block Node View Component
function HTMLBlockNodeView({ node }: any) {
    const htmlContent = node.attrs.html || '';

    // Check if content is SVG - if so, render it directly without wrapper
    const isSVG = htmlContent.trim().startsWith('<svg');

    if (isSVG) {
        return (
            <NodeViewWrapper>
                <div
                    contentEditable={false}
                    suppressContentEditableWarning
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
            </NodeViewWrapper>
        );
    }

    return (
        <NodeViewWrapper>
            <div
                className="html-block-wrapper"
                contentEditable={false}
                suppressContentEditableWarning
                style={{
                    border: '1px dashed #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    margin: '8px 0',
                    backgroundColor: '#f9f9f9',
                    position: 'relative',
                    userSelect: 'none',
                    pointerEvents: 'none',
                }}
            >
                {/* Label to indicate this is an HTML block */}
                <div
                    style={{
                        fontSize: '10px',
                        color: '#999',
                        marginBottom: '4px',
                        fontFamily: 'monospace',
                    }}
                >
                    HTML Block
                </div>

                {/* Render the HTML content */}
                <div
                    className="html-block-content"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                    style={{
                        padding: '4px',
                    }}
                />
            </div>
        </NodeViewWrapper>
    );
}

/**
 * Custom Tiptap extension for raw HTML blocks
 *
 * Allows existing HTML content in markdown documents to be preserved
 * and displayed without breaking the editor
 */
export const HTMLBlock = Node.create({
    name: 'htmlBlock',
    group: 'block',
    atom: true,
    draggable: true,

    addAttributes() {
        return {
            html: {
                default: '',
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-html-block]',
                getAttrs: (dom) => {
                    if (dom instanceof HTMLElement) {
                        return {
                            html: dom.getAttribute('data-html') || dom.innerHTML,
                        };
                    }
                    return {};
                },
            },
        ];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-html-block': '',
                'data-html': node.attrs.html,
            }),
            // Store HTML as text in a hidden pre tag for markdown serialization
            ['pre', { style: 'display: none;' }, node.attrs.html],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(HTMLBlockNodeView);
    },

    addCommands() {
        return {
            setHTMLBlock:
                (html: string): Command =>
                ({ commands }: any) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: { html },
                    });
                },
        };
    },
});

/**
 * Detect HTML blocks in markdown content
 *
 * @param markdown - Markdown string
 * @returns Array of HTML blocks found
 */
export function detectHTMLBlocks(markdown: string): string[] {
    const htmlBlockRegex = /<(div|span|p|h[1-6]|table|ul|ol|mermaid)[^>]*>[\s\S]*?<\/\1>/gi;
    const matches = markdown.match(htmlBlockRegex);
    return matches || [];
}

/**
 * Convert HTML blocks in markdown to HTMLBlock nodes
 *
 * This should be called after markdown deserialization to convert
 * raw HTML into HTMLBlock nodes
 */
export function convertHTMLBlocks(content: any): any {
    if (Array.isArray(content)) {
        return content.map((node) => convertHTMLBlocks(node));
    }

    if (typeof content === 'object' && content !== null) {
        // Check if this is a paragraph containing HTML
        if (content.type === 'paragraph' && content.content) {
            const textContent = content.content
                .filter((child: any) => child.type === 'text')
                .map((child: any) => child.text)
                .join('');

            // Check if text looks like HTML (starts with < and ends with >)
            if (textContent.match(/^\s*<[^>]+>[\s\S]*<\/[^>]+>\s*$/)) {
                // Convert to HTML block node
                return {
                    type: 'htmlBlock',
                    attrs: { html: textContent.trim() },
                };
            }
        }

        // Recursively process children
        if (content.content) {
            return {
                ...content,
                content: convertHTMLBlocks(content.content),
            };
        }
    }

    return content;
}
